import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { sendChatMessage as sendChatMessageApi } from '../services/chatbotApi';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  type: 'text' | 'voice';
}

export type ChatMode = 'chatbot' | 'voicebot';

export interface AppState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  chatMode: ChatMode;
  messages: ChatMessage[];
  chatbotMessages: ChatMessage[];
  voicebotMessages: ChatMessage[];
  currentChatId: string | null;
  isRecording: boolean;
  isProcessing: boolean;
}

type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGOUT' }
  | { type: 'SET_CHAT_MODE'; payload: ChatMode }
  | { type: 'ADD_MESSAGE'; payload: ChatMessage }
  | { type: 'ADD_CHATBOT_MESSAGE'; payload: ChatMessage }
  | { type: 'ADD_VOICEBOT_MESSAGE'; payload: ChatMessage }
  | { type: 'SET_CHAT_ID'; payload: string | null }
  | { type: 'SET_RECORDING'; payload: boolean }
  | { type: 'SET_PROCESSING'; payload: boolean }
  | { type: 'CLEAR_MESSAGES' }
  | { type: 'CLEAR_CHATBOT_MESSAGES' }
  | { type: 'CLEAR_VOICEBOT_MESSAGES' };

// Load user from localStorage on initialization
const getInitialUser = (): User | null => {
  try {
    const savedUser = localStorage.getItem('voice-chat-assistant-bot-user');
    return savedUser ? JSON.parse(savedUser) : null;
  } catch (error) {
    console.error('Error loading user from localStorage:', error);
    return null;
  }
};

const initialUser = getInitialUser();

const initialState: AppState = {
  user: initialUser,
  isAuthenticated: !!initialUser,
  isLoading: false,
  chatMode: 'chatbot',
  messages: [],
  chatbotMessages: [],
  voicebotMessages: [],
  currentChatId: null,
  isRecording: false,
  isProcessing: false,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'LOGIN_SUCCESS':
      // Save user to localStorage
      try {
        localStorage.setItem('voice-chat-assistant-bot-user', JSON.stringify(action.payload));
      } catch (error) {
        console.error('Error saving user to localStorage:', error);
      }
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      // Clear user from localStorage
      try {
        localStorage.removeItem('voice-chat-assistant-bot-user');
      } catch (error) {
        console.error('Error removing user from localStorage:', error);
      }
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        messages: [],
        chatbotMessages: [],
        voicebotMessages: [],
        currentChatId: null,
        isRecording: false,
        isProcessing: false,
      };
    case 'SET_CHAT_MODE':
      return { ...state, chatMode: action.payload };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'ADD_CHATBOT_MESSAGE':
      return { ...state, chatbotMessages: [...state.chatbotMessages, action.payload] };
    case 'ADD_VOICEBOT_MESSAGE':
      return { ...state, voicebotMessages: [...state.voicebotMessages, action.payload] };
    case 'SET_CHAT_ID':
      return { ...state, currentChatId: action.payload };
    case 'SET_RECORDING':
      return { ...state, isRecording: action.payload };
    case 'SET_PROCESSING':
      return { ...state, isProcessing: action.payload };
    case 'CLEAR_MESSAGES':
      return { ...state, messages: [] };
    case 'CLEAR_CHATBOT_MESSAGES':
      return { ...state, chatbotMessages: [], currentChatId: null };
    case 'CLEAR_VOICEBOT_MESSAGES':
      return { ...state, voicebotMessages: [] };
    default:
      return state;
  }
}

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  sendMessage: (content: string, type: 'text' | 'voice') => void;
  sendChatbotMessage: (content: string) => Promise<void>;
  startRecording: () => void;
  stopRecording: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

// Dummy users for testing
const dummyUsers = [
  {
    id: '1',
    name: 'John Doe',
    email: 'john@gmail.com',
    password: 'password123',
    avatar: 'J',
  },
  {
    id: '2',
    name: 'Lisa Wilson',
    email: 'lisa@gmail.com',
    password: 'password123',
    avatar: 'S',
  },
  {
    id: '3',
    name: 'Mike Johnson',
    email: 'mike@gmail.com',
    password: 'password123',
    avatar: 'M',
  },
  {
    id: '4',
    name: 'Tom Frontera',
    email: 'tom@gmail.com',
    password: 'password123',
    avatar: 'E',
  },
  {
    id: '5',
    name: 'Muhammad Ahmed',
    email: 'ahmed@gmail.com',
    password: 'password123',
    avatar: 'A',
  },
  {
    id: '6',
    name: 'Admin',
    email: 'admin@example.com',
    password: 'admin123',
    avatar: 'A',
  },
];

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const login = async (email: string, password: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Find user by email and password
    const user = dummyUsers.find(u => u.email === email && u.password === password);
    
    if (!user) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw new Error('Invalid email or password');
    }
    
    const userData: User = {
      id: user.id,
      name: user.name,
      email: user.email,
      avatar: user.avatar,
    };
    
    dispatch({ type: 'LOGIN_SUCCESS', payload: userData });
  };

  const logout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  const sendMessage = (content: string, type: 'text' | 'voice') => {
    const message: ChatMessage = {
      id: Date.now().toString(),
      content,
      sender: 'user',
      timestamp: new Date(),
      type,
    };
    
    dispatch({ type: 'ADD_MESSAGE', payload: message });
    
    // Simulate assistant response
    setTimeout(() => {
      const response: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: `I received your ${type} message: "${content}". How can I help you with your travel needs?`,
        sender: 'assistant',
        timestamp: new Date(),
        type: 'text',
      };
      dispatch({ type: 'ADD_MESSAGE', payload: response });
    }, 1000);
  };

  const sendChatbotMessage = async (content: string) => {
    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content,
      sender: 'user',
      timestamp: new Date(),
      type: 'text',
    };
    
    dispatch({ type: 'ADD_CHATBOT_MESSAGE', payload: userMessage });
    dispatch({ type: 'SET_PROCESSING', payload: true });
    
    try {
      const response = await sendChatMessageApi(content, state.currentChatId || undefined);
      
      // Update chat ID
      dispatch({ type: 'SET_CHAT_ID', payload: response.chatId });
      
      // Add assistant response
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: response.response,
        sender: 'assistant',
        timestamp: new Date(),
        type: 'text',
      };
      
      dispatch({ type: 'ADD_CHATBOT_MESSAGE', payload: assistantMessage });
    } catch (error) {
      console.error('Error sending chatbot message:', error);
      
      // Add error message with more specific error handling
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: error instanceof Error && error.message.includes('HTTP error') 
          ? 'Sorry, there was a connection error. Please check your internet connection and try again.'
          : 'Oops! Something went wrong. Please try again.',
        sender: 'assistant',
        timestamp: new Date(),
        type: 'text',
      };
      
      dispatch({ type: 'ADD_CHATBOT_MESSAGE', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }
  };

  const startRecording = () => {
    dispatch({ type: 'SET_RECORDING', payload: true });
  };

  const stopRecording = () => {
    dispatch({ type: 'SET_RECORDING', payload: false });
    // Simulate processing voice input
    dispatch({ type: 'SET_PROCESSING', payload: true });
    
    setTimeout(() => {
      dispatch({ type: 'SET_PROCESSING', payload: false });
      sendMessage('Voice message recorded', 'voice');
    }, 2000);
  };

  const value: AppContextType = {
    state,
    dispatch,
    login,
    logout,
    sendMessage,
    sendChatbotMessage,
    startRecording,
    stopRecording,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
